import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { fetchAllVehicles } from '../../../lib/vehicleService';
import { Vehicle, formatPrice, formatMileage } from '../../../data/vehicles';
import { PaymentCalculator } from '../../../components/feature/PaymentCalculator';

const proxyImage = (url: string) =>
  url ? `https://images.weserv.nl/?url=${encodeURIComponent(url)}&w=800&output=webp` : '';

export default function FeaturedInventory() {
  const [isVisible, setIsVisible] = useState(false);
  const [featuredVehicles, setFeaturedVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [imgErrors, setImgErrors] = useState<Record<number, boolean>>({});
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => { if (sectionRef.current) observer.unobserve(sectionRef.current); };
  }, []);

  useEffect(() => {
    fetchAllVehicles()
      .then((data) => {
        // Only show vehicles that are not sold and have a valid image
        const available = data.filter((v) => !v.sold && v.image && v.image.trim() !== '');
        setFeaturedVehicles(available.slice(0, 6));
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleImgError = (id: number) => {
    setImgErrors((prev) => ({ ...prev, [id]: true }));
  };

  return (
    <section id="inventory" ref={sectionRef} className="py-20 sm:py-28 px-4 sm:px-8 lg:px-16 bg-[#0d0d0d]">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className={`text-center mb-12 sm:mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="flex items-center justify-center gap-3 sm:gap-4 mb-4">
            <div className="h-px w-8 sm:w-12 bg-[#C9A84C]"></div>
            <span className="text-[#C9A84C] text-[10px] sm:text-xs tracking-[0.3em] sm:tracking-[0.4em] uppercase" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Browse Our Selection
            </span>
            <div className="h-px w-8 sm:w-12 bg-[#C9A84C]"></div>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-3 sm:mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Vehicles for Every Buyer
          </h2>
          <p className="text-white/70 max-w-2xl mx-auto text-xs sm:text-sm leading-relaxed px-4" style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 300 }}>
            Whether you need a reliable daily driver, a family SUV, a hard-working truck, or something in between — we have options for every lifestyle and every budget.
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24 gap-4">
            <div className="w-10 h-10 border-2 border-[#C9A84C]/30 border-t-[#C9A84C] rounded-full animate-spin"></div>
            <p className="text-white/40 text-xs tracking-widest uppercase" style={{ fontFamily: 'Montserrat, sans-serif' }}>Loading vehicles...</p>
          </div>
        ) : featuredVehicles.length === 0 ? (
          /* ── Empty state ── */
          <div className={`text-center py-20 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="w-20 h-20 flex items-center justify-center border border-[#C9A84C]/20 rounded-sm mx-auto mb-6 bg-[#141414]">
              <i className="ri-car-line text-4xl text-[#C9A84C]/40"></i>
            </div>
            <h3 className="text-2xl font-bold text-white/60 mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Inventory Coming Soon</h3>
            <p className="text-white/30 text-sm mb-8 max-w-md mx-auto" style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 300 }}>
              Our team is adding vehicles to the inventory. Check back soon or contact us to ask about available vehicles.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-3 bg-[#C9A84C] hover:bg-[#e0bc5e] text-[#0d0d0d] px-8 py-3.5 rounded-sm text-xs font-bold tracking-widest uppercase transition-all cursor-pointer whitespace-nowrap hover:scale-105 transform"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              Contact Us
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6 mb-10 sm:mb-12">
            {featuredVehicles.map((vehicle, index) => (
              <div
                key={vehicle.id}
                className={`bg-[#141414] border border-white/5 hover:border-[#C9A84C]/40 rounded-sm overflow-hidden group transition-all duration-700 hover:shadow-xl hover:shadow-[#C9A84C]/5 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${400 + index * 100}ms` }}
              >
                <Link to="/inventory" className="block">
                  <div className="relative w-full h-44 sm:h-56 overflow-hidden bg-[#1a1a1a]">
                    {vehicle.image && !imgErrors[vehicle.id] ? (
                      <img
                        src={proxyImage(vehicle.image)}
                        alt={vehicle.name}
                        className="w-full h-full object-cover object-top scale-[1.12] group-hover:scale-[1.22] transition-transform duration-700"
                        onError={() => handleImgError(vehicle.id)}
                      />
                    ) : (
                      <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
                        <i className="ri-car-line text-4xl text-white/20"></i>
                        <span className="text-white/30 text-xs" style={{ fontFamily: 'Montserrat, sans-serif' }}>No Image Available</span>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                    <div className="absolute top-3 sm:top-4 right-3 sm:right-4 bg-[#C9A84C] px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-sm">
                      <span className="text-[#0d0d0d] text-[10px] sm:text-xs font-bold tracking-wider" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                        {vehicle.condition.toUpperCase()}
                      </span>
                    </div>
                  </div>
                </Link>
                <div className="p-5 sm:p-6">
                  <Link to="/inventory" className="block mb-4">
                    <div className="mb-2 sm:mb-3">
                      <p className="text-[#C9A84C] text-[10px] sm:text-xs tracking-widest uppercase mb-1" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                        {vehicle.brand} · {vehicle.year}
                      </p>
                      <h3 className="text-sm sm:text-base font-semibold text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                        {vehicle.name}
                      </h3>
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-4 sm:mb-5">
                      {[
                        { icon: 'ri-speed-line', label: formatMileage(vehicle.mileage) },
                        { icon: 'ri-settings-3-line', label: vehicle.transmission ?? 'Automatic' },
                        { icon: 'ri-gas-station-line', label: vehicle.fuel },
                      ].map((tag) => (
                        <div key={tag.label} className="flex items-center gap-1 sm:gap-1.5 bg-white/5 px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-sm text-[10px] sm:text-xs text-white/60" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                          <i className={tag.icon}></i>
                          <span>{tag.label}</span>
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center justify-between pt-4 sm:pt-5 border-t border-white/5 mb-4">
                      <div>
                        <p className="text-lg sm:text-xl font-bold text-white" style={{ fontFamily: 'Montserrat, sans-serif', letterSpacing: '-0.02em' }}>
                          {formatPrice(vehicle.price)}
                        </p>
                      </div>
                      <div className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center border border-[#C9A84C]/30 group-hover:bg-[#C9A84C] group-hover:border-[#C9A84C] rounded-sm transition-all">
                        <i className="ri-arrow-right-line text-[#C9A84C] group-hover:text-[#0d0d0d] transition-colors"></i>
                      </div>
                    </div>
                  </Link>

                  {/* Payment Calculator */}
                  <div onClick={(e) => e.stopPropagation()}>
                    <PaymentCalculator vehiclePrice={vehicle.price} isCompact={true} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Can't Find What You're Looking For CTA */}
        <div className={`bg-[#141414] border border-[#C9A84C]/20 rounded-sm p-8 sm:p-10 lg:p-12 mb-10 sm:mb-12 text-center transition-all duration-1000 delay-600 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="max-w-3xl mx-auto">
            <div className="w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center bg-[#C9A84C]/10 rounded-sm mx-auto mb-5">
              <i className="ri-search-line text-2xl sm:text-3xl text-[#C9A84C]"></i>
            </div>
            <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3 sm:mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Can't Find What You're Looking For?
            </h3>
            <p className="text-white/70 text-xs sm:text-sm leading-relaxed mb-6 sm:mb-8 max-w-2xl mx-auto" style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 300 }}>
              We have access to thousands of vehicles beyond what's listed here. Tell us what you need — make, model, year, features — and we'll source it for you.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-3 bg-[#C9A84C] hover:bg-[#e0bc5e] text-[#0d0d0d] px-6 sm:px-8 py-3 sm:py-4 rounded-sm text-xs font-bold tracking-widest uppercase transition-all cursor-pointer whitespace-nowrap hover:scale-105 transform"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              Request a Vehicle
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>

        {/* View All Button */}
        {featuredVehicles.length > 0 && (
          <div className={`text-center transition-all duration-1000 delay-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <Link
              to="/inventory"
              className="inline-flex items-center gap-3 bg-transparent border border-white/20 hover:border-[#C9A84C] text-white hover:text-[#C9A84C] px-6 sm:px-8 py-3 sm:py-4 rounded-sm text-xs font-bold tracking-widest uppercase transition-all cursor-pointer whitespace-nowrap hover:scale-105 transform"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              View Full Inventory
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
